import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { pipelineService, prospectService, advisorService } from '../services/api';
import { PipelineParticipation, PipelineStageTransition, Prospect, Note, AdvisorThread } from '../types';
import { usePipelineStages } from '../services/usePipelineStages';
import { StudentProgress } from '../components/StageProgress';
import {
  connectAdvisorSocket,
  emitAdvisorStopTyping,
  emitAdvisorTyping,
  joinAdvisorThreadRoom,
  leaveAdvisorThreadRoom,
  onAdvisorMessageEvent,
  onAdvisorStopTypingEvent,
  onAdvisorThreadUpdatedEvent,
  onAdvisorTypingEvent,
} from '../services/socket';

const ProspectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newNote, setNewNote] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [isAdvancingToStage, setIsAdvancingToStage] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [selectedStage, setSelectedStage] = useState('');
  const [showStageSelector, setShowStageSelector] = useState(false);
  const [participations, setParticipations] = useState<PipelineParticipation[]>([]);
  const [pipelineTransitions, setPipelineTransitions] = useState<PipelineStageTransition[]>([]);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [pipelineError, setPipelineError] = useState('');
  const [advisorThread, setAdvisorThread] = useState<AdvisorThread>({ messages: [] });
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const [advisorError, setAdvisorError] = useState('');
  const [advisorDraft, setAdvisorDraft] = useState('');
  const [advisorSending, setAdvisorSending] = useState(false);
  const [showAdvisorEmojiPicker, setShowAdvisorEmojiPicker] = useState(false);
  const [studentIsTyping, setStudentIsTyping] = useState(false);
  const [chatDebug, setChatDebug] = useState<{ socket: string; threadId?: number | null; last: string }>({
    socket: 'unknown',
    threadId: null,
    last: 'init',
  });
  const showChatDebug = process.env.NODE_ENV !== 'production';
  const advisorMessagesRef = useRef<HTMLDivElement | null>(null);
  const advisorInputRef = useRef<HTMLTextAreaElement | null>(null);
  const advisorEmojiPickerRef = useRef<HTMLDivElement | null>(null);
  const advisorTypingTimeoutRef = useRef<number | null>(null);
  const studentTypingHideTimeoutRef = useRef<number | null>(null);
  const advisorTypingSentRef = useRef(false);
  const advisorTypingLastEmitAtRef = useRef(0);
  const advisorEmojiOptions = [
    '😀', '😁', '😂', '😊', '🙂', '😉', '😍', '🤩', '😎', '🥳',
    '🤔', '🙌', '👏', '👍', '👎', '🙏', '🤲', '💪', '❤️', '💚',
    '🔥', '✨', '🎉', '🎓', '📚', '📝', '💬', '📌', '✅', '⏳',
    '🌍', '✈️', '🕌', '🤝', '💡', '📈', '🎯', '🚀', '⭐', '🥰',
  ];
  const scrollAdvisorMessagesToBottom = useCallback(() => {
    if (!advisorMessagesRef.current) return;
    requestAnimationFrame(() => {
      if (!advisorMessagesRef.current) return;
      advisorMessagesRef.current.scrollTop = advisorMessagesRef.current.scrollHeight;
    });
  }, []);
  const scheduleAdvisorScrollToBottom = useCallback(() => {
    scrollAdvisorMessagesToBottom();
    window.setTimeout(scrollAdvisorMessagesToBottom, 80);
    window.setTimeout(scrollAdvisorMessagesToBottom, 180);
  }, [scrollAdvisorMessagesToBottom]);

  const [pipelineStages] = usePipelineStages();
  const stages = pipelineStages.map((s: any) => s.code);

  // Helper to get stage info from code
  const getStageInfo = (code: string) => pipelineStages.find((s: any) => s.code === code);

  const fetchProspectDetails = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const [prospectDetailData, notesData] = await Promise.all([
        prospectService.getProspect(parseInt(id)),
        prospectService.getProspectNotes(parseInt(id))
      ]);
      setProspect(prospectDetailData.prospect);
      setNotes(notesData);
    } catch (err) {
      setError('Failed to load prospect details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const handleApplicationStatusChange = async (status: string) => {
    if (!prospect?.id) return;
    setUpdatingStatus(true);
    try {
      await prospectService.updateProspectDetails(prospect.id, { application_status: status });
      await fetchProspectDetails();
    } catch (err) {
      setError('Failed to update application status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchProspectDetails();
    }
  }, [id, fetchProspectDetails]);

  useEffect(() => {
    const loadParticipation = async () => {
      if (!prospect?.user_id) return;
      setPipelineLoading(true);
      setPipelineError('');
      try {
        const data = await pipelineService.getParticipation(prospect.user_id, 'student_v1');
        setParticipations(data.participations || []);
        setPipelineTransitions(data.transitions || []);
      } catch (err) {
        setPipelineError('Unable to load pipeline participation.');
      } finally {
        setPipelineLoading(false);
      }
    };
    loadParticipation();
  }, [prospect?.user_id]);

  const loadAdvisorThread = useCallback(async (prospectId: number) => {
    setAdvisorLoading(true);
    setAdvisorError('');
    try {
      const thread = await advisorService.getThreadAdmin(prospectId);
      setAdvisorThread(thread);
      await advisorService.markThreadReadAdmin(prospectId);
      scheduleAdvisorScrollToBottom();
    } catch (err: any) {
      setAdvisorError(err?.response?.data?.error || 'Unable to load advisor messages.');
    } finally {
      setAdvisorLoading(false);
    }
  }, [scheduleAdvisorScrollToBottom]);

  useEffect(() => {
    if (!prospect?.id) return;
    loadAdvisorThread(prospect.id);
  }, [prospect?.id, loadAdvisorThread]);

  useEffect(() => {
    const client = connectAdvisorSocket();
    if (!client) return;
    setChatDebug((prev) => ({ ...prev, socket: client.connected ? 'connected' : 'connecting' }));
    const onConnect = () => setChatDebug((prev) => ({ ...prev, socket: 'connected', last: 'socket connected' }));
    const onDisconnect = (reason: string) =>
      setChatDebug((prev) => ({ ...prev, socket: `disconnected (${reason})`, last: 'socket disconnected' }));
    client.on('connect', onConnect);
    client.on('disconnect', onDisconnect);
    return () => {
      client.off('connect', onConnect);
      client.off('disconnect', onDisconnect);
    };
  }, []);

  useEffect(() => {
    if (!advisorThread.id) return;
    joinAdvisorThreadRoom(advisorThread.id, (response) => {
      const ok = response?.ok ? 'ok' : `error:${response?.error || 'unknown'}`;
      setChatDebug((prev) => ({ ...prev, threadId: advisorThread.id, last: `join_thread ${ok} id=${advisorThread.id}` }));
    });
    return () => {
      emitAdvisorStopTyping(advisorThread.id as number);
      leaveAdvisorThreadRoom(advisorThread.id as number);
    };
  }, [advisorThread.id]);

  useEffect(() => {
    const unsubscribeNew = onAdvisorMessageEvent((payload: any) => {
      if (!payload || !payload.thread_id) return;
      setAdvisorThread((prev) => {
        if (!prev.id || Number(prev.id) !== Number(payload.thread_id)) return prev;
        if (prev.messages.some((message) => message.id === payload.id)) return prev;
        return {
          ...prev,
          updated_at: payload.created_at || prev.updated_at,
          messages: [...prev.messages, payload],
        };
      });
      setChatDebug((prev) => ({ ...prev, last: `chat:new role=${payload.sender_role} id=${payload.id}` }));
      scheduleAdvisorScrollToBottom();
      if (payload.sender_role === 'student') {
        if (studentTypingHideTimeoutRef.current) {
          window.clearTimeout(studentTypingHideTimeoutRef.current);
          studentTypingHideTimeoutRef.current = null;
        }
        setStudentIsTyping(false);
      }
    });

    const unsubscribeUpdated = onAdvisorThreadUpdatedEvent((payload: any) => {
      if (!payload || !prospect?.id) return;
      if (payload.prospect_id === prospect.id) {
        setChatDebug((prev) => ({ ...prev, last: `chat:thread_updated thread=${payload.thread_id}` }));
        loadAdvisorThread(prospect.id);
      }
    });
    const unsubscribeTyping = onAdvisorTypingEvent((payload: any) => {
      if (!payload || !advisorThread.id) return;
      if (Number(payload.thread_id) !== Number(advisorThread.id)) return;
      if (payload.sender_role !== 'student') return;
      if (studentTypingHideTimeoutRef.current) {
        window.clearTimeout(studentTypingHideTimeoutRef.current);
      }
      setStudentIsTyping(true);
      setChatDebug((prev) => ({ ...prev, last: `chat:typing role=${payload.sender_role}` }));
      studentTypingHideTimeoutRef.current = window.setTimeout(() => {
        setStudentIsTyping(false);
      }, 2200);
    });
    const unsubscribeStopTyping = onAdvisorStopTypingEvent((payload: any) => {
      if (!payload || !advisorThread.id) return;
      if (Number(payload.thread_id) !== Number(advisorThread.id)) return;
      if (payload.sender_role !== 'student') return;
      if (studentTypingHideTimeoutRef.current) {
        window.clearTimeout(studentTypingHideTimeoutRef.current);
      }
      studentTypingHideTimeoutRef.current = window.setTimeout(() => {
        setStudentIsTyping(false);
      }, 450);
      setChatDebug((prev) => ({ ...prev, last: `chat:stop_typing role=${payload.sender_role}` }));
    });

    return () => {
      unsubscribeNew();
      unsubscribeUpdated();
      unsubscribeTyping();
      unsubscribeStopTyping();
    };
  }, [advisorThread.id, prospect?.id, loadAdvisorThread, scheduleAdvisorScrollToBottom]);

  useEffect(() => {
    if (location.hash !== '#advisor-messages') return;
    const el = document.getElementById('advisor-messages');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        scrollAdvisorMessagesToBottom();
      }, 120);
    }
  }, [location.hash, prospect?.id, scrollAdvisorMessagesToBottom]);

  useEffect(() => {
    scrollAdvisorMessagesToBottom();
  }, [advisorThread.id, advisorThread.updated_at, advisorThread.messages.length, scrollAdvisorMessagesToBottom]);

  useEffect(() => {
    if (advisorLoading) return;
    scheduleAdvisorScrollToBottom();
  }, [advisorLoading, scheduleAdvisorScrollToBottom]);

  const formatDateTime = (value?: string) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !newNote.trim()) return;

    setIsAddingNote(true);
    try {
      await prospectService.addProspectNote(parseInt(id), newNote.trim());
      setNewNote('');
      fetchProspectDetails(); // Refresh notes
    } catch (err) {
      setError('Failed to add note');
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleAdvanceStage = async () => {
    if (!prospect || !id) return;

    const currentIndex = stages.indexOf(prospect.current_stage);
    if (currentIndex >= stages.length - 1) return; // Already at final stage

    const nextStage = stages[currentIndex + 1];

    setIsAdvancing(true);
    try {
      await prospectService.advanceProspectToStage(parseInt(id), nextStage, `Advanced to next stage: ${nextStage}`);
      fetchProspectDetails(); // Refresh prospect data
    } catch (err) {
      setError('Failed to advance prospect');
    } finally {
      setIsAdvancing(false);
    }
  };

  const handleJumpToStage = async () => {
    if (!prospect || !id || !selectedStage) return;

    setIsAdvancingToStage(true);
    try {
      await prospectService.advanceProspectToStage(parseInt(id), selectedStage, `Jumped to ${selectedStage} stage`);
      fetchProspectDetails(); // Refresh prospect data
      setShowStageSelector(false);
      setSelectedStage('');
    } catch (err) {
      setError('Failed to advance prospect to stage');
    } finally {
      setIsAdvancingToStage(false);
    }
  };

  const sendAdvisorReply = async () => {
    if (!prospect?.id) return;
    const body = advisorDraft.trim();
    if (!body) return;
    setAdvisorSending(true);
    setAdvisorError('');
    try {
      await advisorService.replyAdmin(prospect.id, body);
      if (advisorThread.id) {
        emitAdvisorStopTyping(advisorThread.id, () => {
          setChatDebug((prev) => ({ ...prev, last: 'emit stop_typing ack (send)' }));
        });
      }
      advisorTypingSentRef.current = false;
      advisorTypingLastEmitAtRef.current = 0;
      if (advisorTypingTimeoutRef.current) {
        window.clearTimeout(advisorTypingTimeoutRef.current);
        advisorTypingTimeoutRef.current = null;
      }
      setAdvisorDraft('');
      await loadAdvisorThread(prospect.id);
    } catch (err: any) {
      setAdvisorError(err?.response?.data?.error || 'Failed to send reply.');
    } finally {
      setAdvisorSending(false);
    }
  };

  const handleAdvisorReply = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendAdvisorReply();
  };

  useEffect(() => {
    return () => {
      if (advisorTypingTimeoutRef.current) {
        window.clearTimeout(advisorTypingTimeoutRef.current);
      }
      if (studentTypingHideTimeoutRef.current) {
        window.clearTimeout(studentTypingHideTimeoutRef.current);
      }
    };
  }, []);

  const handleAdvisorDraftChange = (value: string) => {
    setAdvisorDraft(value);
    if (!advisorThread.id) return;
    const trimmed = value.trim();
    if (!trimmed) {
      emitAdvisorStopTyping(advisorThread.id, () => {
        setChatDebug((prev) => ({ ...prev, last: 'emit stop_typing ack (empty)' }));
      });
      advisorTypingSentRef.current = false;
      advisorTypingLastEmitAtRef.current = 0;
      if (advisorTypingTimeoutRef.current) {
        window.clearTimeout(advisorTypingTimeoutRef.current);
        advisorTypingTimeoutRef.current = null;
      }
      return;
    }
    const now = Date.now();
    if (!advisorTypingSentRef.current || now - advisorTypingLastEmitAtRef.current > 900) {
      emitAdvisorTyping(advisorThread.id, (response) => {
        const ok = response?.ok ? 'ok' : 'no-ok';
        setChatDebug((prev) => ({ ...prev, last: `emit typing ack ${ok}` }));
      });
      advisorTypingSentRef.current = true;
      advisorTypingLastEmitAtRef.current = now;
    }
    if (advisorTypingTimeoutRef.current) {
      window.clearTimeout(advisorTypingTimeoutRef.current);
    }
    advisorTypingTimeoutRef.current = window.setTimeout(() => {
      if (!advisorThread.id) return;
      emitAdvisorStopTyping(advisorThread.id, () => {
        setChatDebug((prev) => ({ ...prev, last: 'emit stop_typing ack (timeout)' }));
      });
      advisorTypingSentRef.current = false;
      advisorTypingLastEmitAtRef.current = 0;
    }, 1200);
  };

  const handleAdvisorEmojiInsert = (emoji: string) => {
    handleAdvisorDraftChange(`${advisorDraft}${emoji}`);
    setShowAdvisorEmojiPicker(false);
    window.setTimeout(() => advisorInputRef.current?.focus(), 0);
  };

  const handleAdvisorComposerKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!advisorSending && advisorDraft.trim()) {
        void sendAdvisorReply();
      }
    }
  };

  useEffect(() => {
    const input = advisorInputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    const nextHeight = Math.min(input.scrollHeight, 180);
    input.style.height = `${Math.max(nextHeight, 88)}px`;
    input.style.overflowY = input.scrollHeight > 180 ? 'auto' : 'hidden';
  }, [advisorDraft]);

  useEffect(() => {
    if (!showAdvisorEmojiPicker) return;
    const handleClickOutside = (event: MouseEvent) => {
      const picker = advisorEmojiPickerRef.current;
      if (!picker) return;
      if (!picker.contains(event.target as Node)) {
        setShowAdvisorEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAdvisorEmojiPicker]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="bg-gray-200 h-96 rounded-2xl"></div>
            </div>
            <div>
              <div className="bg-gray-200 h-64 rounded-2xl"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !prospect) {
    return (
      <div className="space-y-6">
        <div className="text-center py-16">
          <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-6">
            <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Prospect Not Found</h3>
          <p className="text-gray-600 mb-6">The requested prospect could not be found.</p>
          <button
            onClick={() => navigate('/admin/prospects')}
            className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-6 py-3"
          >
            Back to Prospects
          </button>
        </div>
      </div>
    );
  }

  if (!prospect) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-2 sm:space-y-0">
        <div>
          <button
            onClick={() => navigate('/admin/prospects')}
            className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-2 transition duration-200"
          >
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back to Prospects
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {prospect.first_name} {prospect.last_name}
          </h1>
          <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">Prospect ID: {prospect.id}</p>
        </div>
        <div className="flex space-x-3">
          {/* Advance to Next Stage */}
          {stages.indexOf(prospect.current_stage) < stages.length - 1 && (
            <button
              onClick={handleAdvanceStage}
              disabled={isAdvancing}
              className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAdvancing ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              )}
              Advance to {getStageInfo(stages[stages.indexOf(prospect.current_stage) + 1])?.title || stages[stages.indexOf(prospect.current_stage) + 1]}
            </button>
          )}

          {/* Jump to Any Stage - Perfect for Alumni Import */}
          {!showStageSelector ? (
            <button
              onClick={() => setShowStageSelector(true)}
              className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-amber-500 text-amber-950 border border-amber-500 hover:bg-amber-600 hover:border-amber-600 px-6 py-3"
              style={{ minWidth: '160px', height: '48px' }}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              <span>Jump to Stage</span>
            </button>
          ) : (
            <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-2 flex items-center space-x-2 min-w-max">
              <select
                value={selectedStage}
                onChange={(e) => setSelectedStage(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 min-w-32"
              >
                <option value="">Select Stage</option>
                {stages.filter(stage => stage !== prospect.current_stage).map(stage => (
                  <option key={stage} value={stage}>
                    {getStageInfo(stage)?.title || stage}
                  </option>
                ))}
              </select>
              <button
                onClick={handleJumpToStage}
                disabled={!selectedStage || isAdvancingToStage}
                className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-amber-500 text-amber-950 border border-amber-500 hover:bg-amber-600 hover:border-amber-600 px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAdvancingToStage ? (
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                ) : (
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
                Jump
              </button>
              <button
                onClick={() => {
                  setShowStageSelector(false);
                  setSelectedStage('');
                }}
                className="text-gray-400 hover:text-gray-600 p-1"
                title="Cancel"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pathway Progress Chart (Business Logic) */}
      <div className="mb-8">
        <StudentProgress currentStage={prospect.current_stage} useBusinessTerms />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Prospect Information */}
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Prospect information</h3>
              <div className="text-right">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-800">
                  {getStageInfo(prospect.current_stage)?.title || prospect.current_stage}
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  Business Stage: {getStageInfo(prospect.current_stage)?.businessTitle || 'Unknown'}
                </p>
              </div>
            </div>
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Full Name</label>
                <p className="text-slate-900 dark:text-slate-100 font-semibold">{prospect.first_name} {prospect.last_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email</label>
                <p className="text-slate-900 dark:text-slate-100">{prospect.email || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Phone</label>
                <p className="text-slate-900 dark:text-slate-100">{prospect.phone || 'Not provided'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Program Interest</label>
                <p className="text-slate-900 dark:text-slate-100">{prospect.program_interest ? (getStageInfo(prospect.program_interest)?.title || prospect.program_interest) : 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Application Status</label>
                <div className="flex items-center gap-2">
                  <select
                    value={prospect.application_status || 'applied'}
                    onChange={(e) => handleApplicationStatusChange(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    disabled={updatingStatus}
                  >
                    <option value="applied">Applied</option>
                    <option value="accepted">Accepted</option>
                    <option value="enrolled">Enrolled</option>
                    <option value="withdrawn">Withdrawn</option>
                  </select>
                  {updatingStatus && (
                    <span className="text-xs text-slate-500 dark:text-slate-400">Saving…</span>
                  )}
                </div>
                {prospect.accepted_at && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Accepted on {new Date(prospect.accepted_at).toLocaleDateString()}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Commitment Level</label>
                <p className="text-slate-900 dark:text-slate-100">{prospect.commitment_level ? (getStageInfo(prospect.commitment_level)?.title || prospect.commitment_level) : 'Not provided'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Timeline</label>
                <p className="text-slate-900 dark:text-slate-100">{prospect.timeline ? (getStageInfo(prospect.timeline)?.title || prospect.timeline) : 'Not provided'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">How did you hear about us?</label>
                <p className="text-slate-900 dark:text-slate-100">{prospect.how_did_you_hear ? (getStageInfo(prospect.how_did_you_hear)?.title || prospect.how_did_you_hear) : 'Not provided'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Created Date</label>
                <p className="text-slate-900 dark:text-slate-100">{new Date(prospect.created_at).toLocaleDateString()}</p>
              </div>
            </div>

              {prospect.program_motivation && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Program Motivation</label>
                  <p className="text-slate-900 dark:text-slate-100 bg-gray-50 dark:bg-slate-800/60 p-4 rounded-lg whitespace-pre-wrap">{prospect.program_motivation}</p>
                </div>
              )}

              {prospect.additional_info && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Additional Information</label>
                  <p className="text-gray-900 bg-gray-50 p-4 rounded-lg">{prospect.additional_info}</p>
                </div>
              )}
            </div>
          </div>

          {/* Notes Section */}
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Notes & communications</h3>
            </div>
            <div className="p-8">
              {/* Add Note Form */}
              <form onSubmit={handleAddNote} className="mb-6">
                <div className="flex space-x-3">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note about this prospect..."
                  rows={3}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100"
                />
                <button
                  type="submit"
                  disabled={isAddingNote || !newNote.trim()}
                  className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAddingNote ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    'Add Note'
                  )}
                </button>
                </div>
              </form>

              {/* Notes List */}
              <div className="space-y-4">
                {notes.length > 0 ? (
                  notes.map((note) => (
                    <div key={note.id} className="bg-gray-50 dark:bg-slate-800/60 p-4 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">System Note</span>
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                          {new Date(note.created_at).toLocaleDateString()} at{' '}
                          {new Date(note.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-slate-700 dark:text-slate-200">{note.content}</p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <p className="text-slate-500 dark:text-slate-400">No notes added yet. Add the first note to start tracking communications.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Advisor Messages */}
          <div id="advisor-messages" className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Advisor messages</h3>
              <button
                type="button"
                onClick={() => prospect?.id && loadAdvisorThread(prospect.id)}
                className="text-sm font-semibold text-emerald-600 hover:text-emerald-700"
              >
                Refresh
              </button>
            </div>
            <div className="p-8">
              {advisorLoading ? (
                <div className="text-sm text-gray-500 dark:text-slate-300">Loading messages...</div>
              ) : (
                <div ref={advisorMessagesRef} className="space-y-3 mb-6 max-h-[52vh] sm:max-h-96 overflow-y-auto pr-1">
                  {advisorThread.messages.length === 0 ? (
                    <div className="text-sm text-gray-500 dark:text-slate-300">No advisor messages yet.</div>
                  ) : (
                    advisorThread.messages.map((message) => {
                      const isAdmin = message.sender_role === 'admin';
                      const senderLabel = isAdmin ? (message.sender_name || 'Advisor Team') : 'Student';
                      const timestamp = message.created_at ? new Date(message.created_at).toLocaleString() : '';
                      return (
                        <div key={message.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                          <div
                            className={`max-w-[86%] rounded-xl border px-4 py-3 text-sm leading-6 ${
                              isAdmin
                                ? 'bg-emerald-600 text-white border-emerald-500'
                                : 'bg-gray-50 text-gray-800 border-gray-200 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600'
                            }`}
                          >
                            <div className={`text-xs font-semibold mb-1 ${isAdmin ? 'text-emerald-100' : 'text-gray-500 dark:text-slate-300'}`}>
                              {timestamp ? `${senderLabel} • ${timestamp}` : senderLabel}
                            </div>
                            <p className="whitespace-pre-wrap break-words">{message.body}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
              {advisorError && (
                <div className="text-sm text-red-600 mb-4">{advisorError}</div>
              )}
              <form onSubmit={handleAdvisorReply} className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200">Reply as advisor</label>
                <div className="relative" ref={advisorEmojiPickerRef}>
                  <textarea
                    ref={advisorInputRef}
                    value={advisorDraft}
                    onChange={(e) => handleAdvisorDraftChange(e.target.value)}
                    onKeyDown={handleAdvisorComposerKeyDown}
                    rows={3}
                    placeholder="Write a reply to the student..."
                    className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600 focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowAdvisorEmojiPicker((prev) => !prev)}
                    className="absolute bottom-2 right-2 inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 dark:border-slate-600 bg-white/90 dark:bg-slate-900/90 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                    title="Add emoji"
                    aria-label="Add emoji"
                  >
                    🙂
                  </button>
                  {showAdvisorEmojiPicker && (
                    <div className="absolute bottom-12 right-0 z-20 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg p-2">
                      <div className="grid grid-cols-5 gap-1">
                        {advisorEmojiOptions.map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => handleAdvisorEmojiInsert(emoji)}
                            className="h-8 w-8 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
                            aria-label={`Insert ${emoji}`}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {studentIsTyping && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-300 inline-flex items-center gap-2">
                    <span>Student is typing</span>
                    <span className="inline-flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="h-1.5 w-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '120ms' }} />
                      <span className="h-1.5 w-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '240ms' }} />
                    </span>
                  </p>
                )}
                {showChatDebug && (
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 font-mono">
                    debug socket:{chatDebug.socket} thread:{chatDebug.threadId ?? '-'} last:{chatDebug.last}
                  </p>
                )}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={advisorSending || !advisorDraft.trim()}
                    className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-5 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {advisorSending ? 'Sending...' : 'Send Reply'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Pipeline Progress */}
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <h4 className="text-lg font-medium text-slate-900 dark:text-slate-50">Pipeline progress</h4>
            </div>
            <div className="p-6 space-y-3">
              {stages.map((stage, index) => {
                const isCurrentStage = stage === prospect.current_stage;
                const isCompleted = stages.indexOf(prospect.current_stage) > index;
                
                return (
                  <div key={stage} className="flex items-center">
                    <div className={`w-4 h-4 rounded-full mr-3 ${
                      isCurrentStage 
                        ? 'bg-blue-600 ring-4 ring-blue-200' 
                        : isCompleted 
                        ? 'bg-green-500' 
                        : 'bg-gray-300'
                    }`}></div>
                    <span className={`text-sm ${
                      isCurrentStage ? 'font-semibold text-blue-600' : 'text-gray-700'
                    }`}>
                      {getStageInfo(stage)?.title || stage}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pipeline Participation */}
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <h4 className="text-lg font-medium text-slate-900 dark:text-slate-50">Pipeline participation</h4>
            </div>
            <div className="p-6">
              {pipelineLoading ? (
                <div className="text-sm text-gray-500">Loading participation…</div>
              ) : pipelineError ? (
                <div className="text-sm text-red-500">{pipelineError}</div>
              ) : (
                <>
                  {participations.length === 0 ? (
                    <div className="text-sm text-gray-500">No participation records found.</div>
                  ) : (
                    <div className="space-y-3">
                      {participations.map((p) => (
                        <div key={p.id} className="rounded-lg border border-gray-200 px-3 py-2">
                          <div className="text-xs uppercase tracking-wide text-gray-500">Pipeline</div>
                          <div className="font-semibold text-gray-900">{p.pipeline_id}</div>
                          <div className="mt-2 text-sm text-gray-600">
                            Stage: <span className="font-semibold text-gray-900">{getStageInfo(p.current_stage)?.title || p.current_stage}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Updated {formatDateTime(p.updated_at)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {pipelineTransitions.length > 0 && (
                    <div className="mt-4">
                      <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Recent Transitions</div>
                      <div className="space-y-2">
                        {pipelineTransitions.slice(0, 4).map((t) => (
                          <div key={t.id} className="text-sm text-gray-700">
                            <span className="font-semibold">{getStageInfo(t.from_stage || '')?.title || t.from_stage || 'Start'}</span>
                            {' → '}
                            <span className="font-semibold">{getStageInfo(t.to_stage)?.title || t.to_stage}</span>
                            <span className="text-xs text-gray-500"> · {formatDateTime(t.transition_date)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <h4 className="text-lg font-medium text-slate-900 dark:text-slate-50">Quick actions</h4>
            </div>
            <div className="p-6 space-y-3">
              <button 
                onClick={() => navigate(`/admin/prospects/${id}/questionnaire`)}
                className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 w-full px-3 py-2 text-sm"
              >
                <svg className="w-4 h-4 mr-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2v.01h12V6H4zm0 2v2h12V8H4zm0 4v2h12v-2H4z" clipRule="evenodd" />
                </svg>
                View Complete Questionnaire
              </button>
              <button 
                onClick={() => setShowStageSelector(!showStageSelector)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md border border-gray-200 flex items-center transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Advance to Stage
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md border border-gray-200 flex items-center transition-colors duration-200">
                <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C17.759 8.071 18 9.007 18 10zm-9.442 3.442l1.525 1.524A5.987 5.987 0 0110 16a5.987 5.987 0 01-2.077-.034l1.525-1.524a3.997 3.997 0 00-.078-2.183l-1.562-1.562A5.99 5.99 0 006 10c0-.993.241-1.929.668-2.754l1.524 1.525a3.997 3.997 0 00-.078 2.183l1.562 1.562zM10 2a8 8 0 00-6.758 12.188l1.524-1.525A5.987 5.987 0 012 10a5.99 5.99 0 011.808-4.246l1.562 1.562a3.997 3.997 0 002.183-.078L6.029 5.714A7.98 7.98 0 0110 2z" clipRule="evenodd" />
                </svg>
                Contact Prospect
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md border border-gray-200 flex items-center transition-colors duration-200">
                <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2v.01h12V6H4zm0 2v2h12V8H4zm0 4v2h12v-2H4z" clipRule="evenodd" />
                </svg>
                Generate Report
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <h4 className="text-lg font-medium text-slate-900 dark:text-slate-50">Quick stats</h4>
            </div>
            <div className="p-6 space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Notes</span>
                <span className="font-semibold text-gray-900">{notes.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Days in Pipeline</span>
                <span className="font-semibold text-gray-900">
                  {Math.floor((new Date().getTime() - new Date(prospect.created_at).getTime()) / (1000 * 60 * 60 * 24))}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Current Stage</span>
                <span className="font-semibold text-gray-900">{getStageInfo(prospect.current_stage)?.title || prospect.current_stage}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProspectDetail;
